import { FeedbacksPageResults } from '../../interfaces/feedback.interface'
import { STANDARD_INSIGHTS_CACHE_TTL_MINUTES } from './insightsStandardCache'

const CACHE_TTL_MS = STANDARD_INSIGHTS_CACHE_TTL_MINUTES * 60 * 1000
const CACHE_KEY_PREFIX = 'zenbox.happiness-report-feedbacks-standard-cache:v1:'

export type FeedbacksCacheOptions = {
  enabled?: boolean;
  userId?: string;
  scope?: string;
}

type FeedbacksCacheEntry = {
  cachedAtUtc: string;
  data: FeedbacksPageResults;
}

const sortForSerialization = (value: any): any => {
  if (Array.isArray(value)) return value.map(sortForSerialization)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = sortForSerialization(value[key])
      return result
    }, {} as Record<string, any>)
  }
  return value
}

const serialize = (value: any) => JSON.stringify(sortForSerialization(value))

export const buildFeedbacksStandardCacheKey = ({ userId, companyId, month, year, page, scope, request }: {
  userId: string;
  companyId: any;
  month: string;
  year: string;
  page: string;
  scope: string;
  request: any;
}) => {
  const normalizedRequest = {
    ...(request || {}),
    ageRanges: request?.ageRanges ?? [],
    sexes: request?.sexes ?? [],
    groups: request?.groups ?? []
  }
  return `${CACHE_KEY_PREFIX}${encodeURIComponent(serialize({ userId, companyId, month, year, page, scope, request: normalizedRequest }))}`
}

const getStorage = () => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch (_) {
    return null
  }
}

export const clearHappinessReportFeedbacksStandardCache = () => {
  const storage = getStorage()
  if (!storage) return
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => {
      try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
    })
  } catch (_) { /* cache is best effort */ }
}

export const readFeedbacksStandardCache = (key: string): FeedbacksPageResults | null => {
  const storage = getStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as FeedbacksCacheEntry
    const cachedAt = Date.parse(entry?.cachedAtUtc)
    if (!Number.isFinite(cachedAt) || !entry?.data || !Array.isArray(entry.data.data)) {
      try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
      return null
    }
    if (Date.now() - cachedAt < CACHE_TTL_MS) return entry.data
    try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
  } catch (_) {
    try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
  }
  return null
}

const removeExpiredEntries = (storage: Storage) => {
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => {
      try {
        const entry = JSON.parse(storage.getItem(key) || '') as FeedbacksCacheEntry
        const cachedAt = Date.parse(entry?.cachedAtUtc)
        if (!Number.isFinite(cachedAt) || Date.now() - cachedAt >= CACHE_TTL_MS) storage.removeItem(key)
      } catch (_) {
        try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
      }
    })
  } catch (_) { /* cache is best effort */ }
}

export const writeFeedbacksStandardCache = (key: string, data: FeedbacksPageResults) => {
  const storage = getStorage()
  if (!storage) return
  try {
    removeExpiredEntries(storage)
    storage.setItem(key, JSON.stringify({ cachedAtUtc: new Date().toISOString(), data }))
  } catch (_) { /* cache is best effort */ }
}
